import { HeadProps, PageProps, graphql } from 'gatsby'
import React, { useMemo } from 'react'
import { IGatsbyImageData } from 'gatsby-plugin-image'
import { cx } from 'class-variance-authority'
import { AppHead, Layout } from '../components/common'
import {
  GraphqlToolFirmType,
  graphqlToolFirmMapper,
  graphqlToolMapper,
} from '../components/tools/mapper'
import { FirmTeaserProps } from '../components/firms'
import { ToolTabs } from '../components/tools/tabs/ToolTabs'
import { TeaserProps } from '../components/offers/teaser'
import { ToolHeader } from '../components/tools/Toolheader'
import { ModalWithMedias } from '../components/ui/medias/ModalWithMedia'
import Description from '../components/ui/description'

const Tool: React.FC<PageProps<Queries.ToolQuery>> = ({ data, location }) => {
  const mappedTool = useMemo(() => graphqlToolMapper(data), [data])
  const teaserFirms = useMemo(() => (data?.allStrapiAccountingFirm?.nodes || [])
    .map((firm) => graphqlToolFirmMapper(firm as GraphqlToolFirmType))
    .filter(Boolean) as FirmTeaserProps[], [data])

  const offers: TeaserProps[] = useMemo(() => {
    const offersArray = data?.allStrapiAccountingOffer?.nodes || []
    return offersArray
      .filter(Boolean)
      .map(offer => ({
        city: offer.office?.city || undefined,
        companyName: offer.office?.firm?.name || '',
        contractType: offer.contractType || undefined,
        headerImage: offer.office?.firm?.header?.url || undefined,
        logo: offer.office?.firm?.logo?.url || undefined,
        salary: offer.payRange || undefined,
        startDate: offer.startDate || '',
        slug: offer.slug || '',
        title: offer.title || '',
        url: `/cabinets/${offer.office?.firm?.slug}/offres/${offer.slug}`,
      }))
  }, [data])

  if (!mappedTool) {
    return null
  }

  const youTubeApiKey = data.site?.siteMetadata?.youTubeApiKey || undefined

  return (
    <Layout
      apiToken={data.site?.siteMetadata?.frontStrapiToken || undefined}
      apiUrl={data.site?.siteMetadata?.siteStrapiUrl || undefined}
      location={location}
    >
      <div className="h-full bg-very-light-blue">
        <ToolHeader
          name={mappedTool.name}
          logo={mappedTool.logo}
          header={mappedTool.headerImage}
        />
        <ToolTabs
          location={location}
          tool={mappedTool}
          offers={offers}
          firms={teaserFirms}
        />
        <div
          className={cx(
            'grid grid-flow-row grid-cols-1 gap-4 mb-20 auto-rows-max mt-6',
            mappedTool.media && mappedTool.description
              ? 'lg:grid-cols-2'
              : 'lg:grid-cols-1',
            !mappedTool.media && !mappedTool.description ? 'hidden' : '',
            mappedTool.media && !mappedTool.description
              ? 'lg:grid-cols-1'
              : '',
            mappedTool.description && !mappedTool.media
              ? 'lg:grid-cols-1'
              : '',
          )}
        >
          {mappedTool.media && (
            <ModalWithMedias
              id={mappedTool.media?.id}
              media={
                mappedTool.media?.media as
                | IGatsbyImageData
                | string
                | null
              }
              video={mappedTool.media?.video}
              name={mappedTool.name}
              youTubeApiKey={youTubeApiKey}
            />
          )}
          {mappedTool.description && (
            <Description
              data={mappedTool.description as string}
              label={`Qu'est ce que ${mappedTool.name} ?`}
            />
          )}
        </div>
      </div>
    </Layout>
  )
}

export default Tool

export const Head: React.FC<HeadProps<Queries.ToolQuery>> = ({ data }) => {
  const toolSlug = data?.strapiAccountingTool?.permalink
    ? data?.strapiAccountingTool?.permalink.split('/').pop()
    : null
  const linkCanonical = `${data.site?.siteMetadata?.siteUrl}/outils/${toolSlug}`
  const metaImage =
    data.strapiAccountingTool?.header?.url || '/lamacompta-sharing-default.jpg'
  const title = `Les cabinets d'expertise comptable qui utilisent ${data.strapiAccountingTool?.name} sur Lamacompta`
  return (
    <AppHead
      title={title}
      metaDescription={`Rejoignez un cabinet qui utilise ${data.strapiAccountingTool?.name}, sur Lamacompta.`}
      metaTitle={title}
      linkCanonical={linkCanonical}
      metaImage={metaImage}
    />
  )
}

export const query = graphql`
  query Tool($strapiId: Int, $name: String) {
    site {
      siteMetadata {
        siteUrl
        frontStrapiToken
        siteStrapiUrl
        youTubeApiKey
      }
    }
    strapiAccountingTool(strapi_id: { eq: $strapiId }) {
      name
      permalink
      uuid
      logo {
        localFile {
          url
          childImageSharp {
            gatsbyImageData
          }
        }
      }
      description {
        data {
          description
        }
      }
      header {
        url
        localFile {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
      medias {
        url
        id
        local {
          url
          mime
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
    }
    allStrapiAccountingOffer(
      filter: {
        accountingTools: { elemMatch: { name: { eq: $name } } }
        publishedAt: { ne: null }
        office: { firm: { name: { nin: ["Lamacompta", "Cabinet Lama & Associés"] } } }
      }
    ) {
      nodes {
        id
        uuid
        startDate
        title
        slug
        positionFilled
        publishedAt
        payRange {
          min
          max
        }
        contractType
        jobType {
          value
        }
        office {
          city
          firm {
            name
            slug
            header {
              url
              localFile {
                childImageSharp {
                  gatsbyImageData
                }
              }
            }
            logo {
              url
              localFile {
                childImageSharp {
                  gatsbyImageData
                }
              }
            }
          }
        }
      }
    }
    allStrapiAccountingFirm(
      filter: {
        accountingTools: { elemMatch: { name: { eq: $name } } }
        name: { nin: ["Lamacompta", "Cabinet Lama & Associés"] }
      }
    ) {
      nodes {
        name
        subscription {
          name
        }
        totalEmployees
        logo {
          url
        }
        subscription {
          name
        }
        header {
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        offices {
          postalCode
          city
          offers {
            id
            uuid
            startDate
            title
            slug
            positionFilled
            payRange {
              min
              max
            }
            jobType {
              value
            }
          }
        }
        slug
      }
      totalCount
    }
  }
`
